import { useCallback, useEffect, useRef } from "react"
import { loadGrecaptcha } from "./loadGrecaptcha"

const BADGE_STYLE_ATTR = "data-form-popup-recaptcha"
const EXECUTE_TIMEOUT_MS = 30000

function hideRecaptchaBadge(): HTMLStyleElement {
  const existing = document.head.querySelector<HTMLStyleElement>(
    `style[${BADGE_STYLE_ATTR}]`
  )
  if (existing) return existing
  const style = document.createElement("style")
  style.setAttribute(BADGE_STYLE_ATTR, "")
  style.textContent = ".grecaptcha-badge { visibility: hidden !important; }"
  document.head.appendChild(style)
  return style
}

export function useInvisibleRecaptcha(
  siteKey: string | undefined,
  active: boolean
) {
  const widgetIdRef = useRef<number | null>(null)
  const pendingRef = useRef<{
    resolve: (token: string) => void
    reject: (error: Error) => void
  } | null>(null)
  const timeoutRef = useRef<number | null>(null)

  const clearExecuteTimeout = () => {
    if (timeoutRef.current == null) return
    window.clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }

  useEffect(() => {
    if (!siteKey || !active) return

    let cancelled = false
    const host = document.createElement("div")
    host.setAttribute("data-form-popup-recaptcha-widget", "")
    // Keep this in the document stacking context so Google’s challenge iframe
    // can paint above the modal. Do not clip it — execute() never calls back
    // if the challenge (or a domain-error widget) is overflow:hidden.
    host.style.cssText =
      "position:fixed;left:0;bottom:0;z-index:2000000000;"
    document.body.appendChild(host)

    const badgeStyle = hideRecaptchaBadge()

    loadGrecaptcha()
      .then((grecaptcha) => {
        if (cancelled) return
        widgetIdRef.current = grecaptcha.render(host, {
          sitekey: siteKey,
          size: "invisible",
          callback: (token) => {
            pendingRef.current?.resolve(token)
            pendingRef.current = null
            clearExecuteTimeout()
            if (widgetIdRef.current != null) grecaptcha.reset(widgetIdRef.current)
          },
          "error-callback": () => {
            pendingRef.current?.reject(new Error("reCAPTCHA failed"))
            pendingRef.current = null
            clearExecuteTimeout()
          },
          "expired-callback": () => {
            pendingRef.current?.reject(new Error("reCAPTCHA expired"))
            pendingRef.current = null
            clearExecuteTimeout()
            if (widgetIdRef.current != null) grecaptcha.reset(widgetIdRef.current)
          },
        })
      })
      .catch(() => {
        /* execute() surfaces the error if the widget never becomes ready */
      })

    return () => {
      cancelled = true
      widgetIdRef.current = null
      clearExecuteTimeout()
      pendingRef.current?.reject(new Error("reCAPTCHA unmounted"))
      pendingRef.current = null
      host.remove()
      if (!document.querySelector("[data-form-popup-recaptcha-widget]")) {
        badgeStyle.remove()
      }
    }
  }, [siteKey, active])

  const execute = useCallback(async () => {
    const started = Date.now()
    while (widgetIdRef.current == null && Date.now() - started < 8000) {
      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    const grecaptcha = window.grecaptcha
    const widgetId = widgetIdRef.current
    if (!grecaptcha?.execute || widgetId == null) {
      throw new Error("reCAPTCHA is not ready")
    }

    return new Promise<string>((resolve, reject) => {
      clearExecuteTimeout()
      pendingRef.current = {
        resolve: (token) => {
          clearExecuteTimeout()
          pendingRef.current = null
          resolve(token)
        },
        reject: (error) => {
          clearExecuteTimeout()
          pendingRef.current = null
          reject(error)
        },
      }
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null
        pendingRef.current?.reject(new Error("reCAPTCHA failed"))
        pendingRef.current = null
        if (widgetIdRef.current != null) {
          try {
            grecaptcha.reset(widgetIdRef.current)
          } catch {
            /* widget may already be gone */
          }
        }
      }, EXECUTE_TIMEOUT_MS)
      try {
        grecaptcha.execute(widgetId)
      } catch (error) {
        pendingRef.current?.reject(
          error instanceof Error ? error : new Error("reCAPTCHA failed")
        )
      }
    })
  }, [])

  return { execute, enabled: Boolean(siteKey) }
}
